import { useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck } from "lucide-react";

/*
  InsuranceProviders — a compact, animated trust row showing the plans
  we accept. Uses each provider's real favicon (via icon.horse) with a
  brand-coloured text wordmark as a graceful fallback if the icon fails
  to load. No proprietary asset scraping — icon.horse serves the public
  favicon each brand ships on their own domain.
*/

const PROVIDERS = [
  { name: "GreenShield",   tag: "Direct-billing partner",   color: "#00A94F", wordmark: "GreenShield",   domain: "greenshield.ca" },
  { name: "RTIP",          tag: "GreenShield-affiliated",   color: "#00A94F", wordmark: "RTIP",          domain: "greenshield.ca" },
  { name: "Sun Life",      tag: "Coverage accepted",        color: "#F1B434", wordmark: "Sun Life",      domain: "sunlife.ca" },
  { name: "Manulife",      tag: "Coverage accepted",        color: "#00A758", wordmark: "Manulife",      domain: "manulife.ca" },
  { name: "Canada Life",   tag: "Coverage accepted",        color: "#C8102E", wordmark: "Canada Life",   domain: "canadalife.com" },
  { name: "Desjardins",    tag: "Coverage accepted",        color: "#00874E", wordmark: "Desjardins",    domain: "desjardins.com" },
  { name: "Blue Cross",    tag: "Regional plans",           color: "#003DA5", wordmark: "Blue Cross",    domain: "bluecross.ca" },
  { name: "Empire Life",   tag: "Group benefits",           color: "#003366", wordmark: "Empire Life",   domain: "empire.ca" },
  { name: "Union Benefits", tag: "Trade/union plans",       color: "#2E6A8A", wordmark: "Union Benefits", domain: null },
];

const Logo = ({ p }) => {
  const [failed, setFailed] = useState(false);
  if (!p.domain || failed) {
    return (
      <p
        className="font-serif text-lg md:text-xl tracking-tight font-bold leading-none"
        style={{ color: p.color, letterSpacing: "-0.02em" }}
      >
        {p.wordmark}
      </p>
    );
  }
  return (
    <img
      src={`https://icon.horse/icon/${p.domain}`}
      alt={`${p.name} logo`}
      className="h-8 md:h-9 w-auto max-w-[130px] object-contain mx-auto"
      onError={() => setFailed(true)}
      loading="lazy"
      draggable={false}
    />
  );
};

const Card = ({ p, i }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-10% 0px" }}
    transition={{ duration: 0.55, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
    whileHover={{ y: -4 }}
    className="group relative rounded-xl border border-line bg-white px-5 py-6 text-center shadow-[0_1px_0_rgba(0,0,0,0.03)] hover:shadow-[0_18px_40px_-16px_rgba(46,106,138,0.35)] transition-shadow flex flex-col items-center justify-between min-h-[150px]"
    data-testid={`insurance-provider-${p.name.toLowerCase().replace(/\s+/g, '-')}`}
  >
    {/* Coloured brand bar */}
    <span
      className="absolute top-0 left-0 right-0 h-1 rounded-t-xl"
      style={{ background: p.color }}
      aria-hidden
    />
    {/* Real logo (with graceful text fallback) */}
    <div className="mt-2 mb-2 flex-1 grid place-items-center">
      <Logo p={p} />
    </div>
    <p className="text-[10px] uppercase tracking-[0.22em] text-ink2">{p.tag}</p>
    {/* accent underline on hover */}
    <span
      className="mt-3 block h-[2px] w-6 group-hover:w-14 transition-all duration-500"
      style={{ background: p.color, opacity: 0.65 }}
    />
  </motion.div>
);

const InsuranceProviders = ({ compact = false, id = "insurance-providers" }) => (
  <section
    id={id}
    data-testid="insurance-providers"
    className={`bg-sand2 ${compact ? "py-14" : "py-20 md:py-24"}`}
  >
    <div className="mx-auto max-w-[1300px] px-5 md:px-10">
      <div className="text-center mb-10 md:mb-12">
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-forest font-semibold"
        >
          <ShieldCheck className="w-3.5 h-3.5 text-terracotta" /> Coverage accepted
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.05 }}
          className="mt-3 font-serif text-forest text-2xl md:text-4xl leading-tight tracking-tight max-w-2xl mx-auto"
        >
          Most private &amp; extended-benefits plans welcome
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-3 text-ink2 text-sm md:text-base max-w-xl mx-auto"
        >
          We work with private-pay clients and most major insurers. Not sure about your plan?
          {" "}
          <a href="/verify-benefits" className="text-terracotta underline decoration-terracotta/40 underline-offset-4 hover:decoration-terracotta">
            Verify your benefits in 60 seconds
          </a>.
        </motion.p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 xl:grid-cols-8 gap-3 md:gap-4">
        {PROVIDERS.map((p, i) => (
          <Card key={p.name} p={p} i={i} />
        ))}
      </div>

      <p className="mt-6 text-center text-[12px] text-ink2/80">
        Logos are property of their respective owners. This page does not imply endorsement.
      </p>
    </div>
  </section>
);

export default InsuranceProviders;
